'use client'

import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ChevronsUpDown } from 'lucide-react'

import type { RouterOutputs } from '@package/api'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { calculateGradeBasedOnXp } from '@helper/utils/business-logic'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { ScoreboardType } from '../index'
import { useAuthClient, useSharedTRPC } from '../../../../context'

type ScoreboardItem = RouterOutputs['lesson']['getScoreboardData']['items'][number]

type GiveGradeModalProps = ScoreboardType & {
  open: boolean
  onOpenChange: (open: boolean) => void | React.Dispatch<React.SetStateAction<boolean>>
  childProfile: ScoreboardItem['child']
  data: Pick<ScoreboardItem, 'learnProgress'>
  onInvalidateScoreboard?: () => void | Promise<void>
}

export function GiveGradeModal({
  open,
  onOpenChange,
  childProfile,
  data,
  onInvalidateScoreboard,
}: GiveGradeModalProps) {
  const authClient = useAuthClient()
  const trpc = useSharedTRPC()

  const { data: session } = authClient.useSession()

  const gradeMutation = useMutation(
    trpc.lesson.gradeLearnProgress.mutationOptions({
      onSuccess: async () => {
        toast.success('Penilaian berhasil diberikan')
        await onInvalidateScoreboard?.()
        resetAndClose()
      },
      onError: (error: { message: string }) => {
        toast.error('Gagal memberikan penilaian')
        setErrorMsgPoin(error.message)
      },
    }),
  )

  const editGradeMutation = useMutation(
    trpc.lesson.editLearnProgressGrade.mutationOptions({
      onSuccess: async () => {
        toast.success('Penilaian berhasil diubah')
        await onInvalidateScoreboard?.()
        resetAndClose()
      },
      onError: (error: { message: string }) => {
        toast.error('Gagal mengubah penilaian')
        setErrorMsgPoin(error.message)
      },
    }),
  )

  const [poinGrade, setPoinGrade] = useState<number | null>(null)
  const [errorMsgPoin, setErrorMsgPoin] = useState<string | null>(null)

  const currentLearnProgress = data.learnProgress
  const isEditMode = !!currentLearnProgress?.gradedAt

  useEffect(() => {
    if (open && isEditMode && currentLearnProgress?.lesson && currentLearnProgress.earnedXps.length > 0) {
      const { grade } = calculateGradeBasedOnXp({
        lessons: [currentLearnProgress.lesson],
        learnProgresses: [currentLearnProgress],
      })
      setPoinGrade(Math.round(grade))
    } else if (!open) {
      setPoinGrade(null)
    }
  }, [open])

  function resetAndClose() {
    setErrorMsgPoin(null)
    onOpenChange(false)
  }

  if (!currentLearnProgress) return null

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) setErrorMsgPoin(null)
        onOpenChange(open)
      }}
    >
      <DialogContent className='srd:z-50 srd:max-w-[450px]'>
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit penilaian' : 'Beri penilaian'}</DialogTitle>
          <DialogDescription className='srd:sr-only'>
            {isEditMode ? 'Edit' : 'Berikan'} penilaian untuk pelajaran {currentLearnProgress.lesson?.title ?? ''}
          </DialogDescription>
        </DialogHeader>

        <div className='srd:flex srd:flex-col srd:gap-y-8 srd:w-full srd:items-center srd:justify-center srd:text-neutral-black'>
          <div className='srd:flex srd:flex-col srd:gap-y-2 srd:w-full'>
            <h6 className={cn(typographyVariants({ variant: 'title-lg' }))}>{currentLearnProgress.lesson?.title}</h6>

            <div className='srd:flex srd:items-center srd:gap-x-2 srd:flex-nowrap srd:shrink-0'>
              <Avatar className='srd:size-10'>
                <AvatarImage src={childProfile.image ?? undefined} />
                <AvatarFallback>{getInitials(childProfile.name)}</AvatarFallback>
              </Avatar>

              <div className='srd:flex srd:flex-col srd:gap-y-1'>
                <p className={cn(typographyVariants({ variant: 'body-md' }))}>{childProfile.name}</p>

                {childProfile.team && (
                  <p className={cn(typographyVariants({ variant: 'label-sm' }))}>{childProfile.team.name}</p>
                )}
              </div>
            </div>
          </div>

          <Field data-invalid={!!errorMsgPoin}>
            <FieldLabel htmlFor='poin-grade'>Poin</FieldLabel>

            <InputGroup>
              <InputGroupInput
                id='poin-grade'
                value={poinGrade?.toString() ?? undefined}
                onChange={(e) => {
                  const value = e.target.value
                  if (value === '' || /^\d+$/.test(value)) {
                    setPoinGrade(value === '' ? 0 : Number(value))
                  }
                }}
                type='text'
                inputMode='numeric'
                pattern='[0-9]*'
                aria-invalid={!!errorMsgPoin}
                placeholder='0 - 100'
              />
              <InputGroupAddon align='inline-end'>
                <InputGroupText>
                  <ChevronsUpDown className='srd:size-4 srd:text-navy-40' />
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>

            <FieldDescription>Masukkan nilai antara 0 sampai 100.</FieldDescription>

            <FieldError>{errorMsgPoin}</FieldError>
          </Field>
        </div>

        <DialogFooter>
          <Button variant='ghost' onClick={() => onOpenChange(false)}>
            Tutup
          </Button>

          <Button
            loading={gradeMutation.isPending || editGradeMutation.isPending}
            onClick={async () => {
              setErrorMsgPoin(null)

              if (poinGrade === null || poinGrade < 0 || poinGrade > 100) {
                setErrorMsgPoin('Poin hanya boleh antara 0 sampai 100')
                return
              }

              if (!session?.user.id) {
                setErrorMsgPoin('Sesi tidak ditemukan, silakan login ulang')
                return
              }

              if (isEditMode) {
                await editGradeMutation.mutateAsync({ learnProgressId: currentLearnProgress.id, poinGrade })
              } else {
                await gradeMutation.mutateAsync({ learnProgressId: currentLearnProgress.id, poinGrade })
              }
            }}
          >
            {isEditMode ? 'Ubah penilaian' : 'Berikan penilaian'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
